<template>
  <div class="clock-bar">
    <div class="toolbar" v-if="selectedHour == null">
      <span>
        {{ name }}
      </span>
    </div>
    
    <div class="hour-captions">
      <div v-if="showingCurrentTimeIndicator" class="current-time-indicator" :style="currentTimeIndicator">
        <i class="pi pi-arrow-up"></i>
        {{ currentTimeString }}
      </div>
      <div v-for="hr in hourLabels" class="hour-caption">
        {{ hr }}
      </div>
    </div>
    
    <div ref="popoverAnchor" style="position: relative; height: 0.3in;">
      <div class="block-groups">
        <div v-for="blockGroup in blockGroups" class="block-group" :style="blockGroupStyle(blockGroup)">
          <div v-for="block in blockGroup.blocks" class="block" :style="blockStyle(block)">
            <div v-for="blend in block.blends" class="block-blend" :style="blockBlendStyle(blend)">
            </div>
          </div>
        </div>
      </div>
      <div ref="fillBlocks" class="fill-blocks">
        <div v-for="(block, index) in fillBlocks" class="fill-block"
          :blockIndex="index"
          @mousedown="onMouseDown(block, index)" 
          @mouseover="onMouseOver(block, index)" 
          @mouseup="onMouseUp(block)"
          @touchstart="onMouseDown(block, index)"
          @touchmove="onTouchMove($event)"
        ></div>
      </div>
      <Popover
        :showingPopover="selectedHour_ != null"
        :popoverAnchor="$refs.popoverAnchor"
        :popoverContainerStyle="popoverContainerStyle()"
        :clickoutIgnoreElems="popoverClickoutIgnoreElems()"
        @update:showingPopover="selectedHour_ = null"
      >
        <div class="hour-group-popover">
          <h3 style="text-align: center; margin-bottom: 15px;">
            {{ formatTime(selectedHour_).timeString }}
          </h3>
          <clock-bar style="flex-grow: 1;"
            :options="options"
            :committed="committed"
            :clock="clock"
            :fillSize="popoverFillSize"
            :selectedOption="selectedOption"
            :selectedHour="selectedHour_"
            :range="[selectedHour_, selectedHour_ + 60]"
            :onChange="onChange"
          ></clock-bar>
        </div>
      </Popover>
    </div>
  </div>
</template>

<script>
import store from "./store.js";
import { zoneDate } from "./timezones.js";
import { stripePattern } from "./appUtility.js";
import { padStart, range, unique } from "./utility.js";
import Popover from "./Popover.vue";

const clockGranularity = 1;

// Disabled for now because the browser doesn't always correctly align the run divs.
// Tested on Edge v107 with a 30 minute force purge at 930am, in a 1920px viewport.
const singleDivRuns = true;

let draggingClockbar = null;
document.addEventListener("mouseup", function() {
  draggingClockbar = null;
});
document.addEventListener('touchend', function() {
  draggingClockbar = null;
});

export default {
  name: "clock-bar",
  components: { Popover },
  props: [
    "name",
    "clock",
    "options",
    "fillSize",
    "popoverFillSize",
    "selectedOption",
    "selectedHour",
    "range",
    "committed",
    "onChange"
  ],
  created() {
    this.pickerToolbar = document.querySelector('.clocks-page--toolbar');
  },
  data() {
    this.singleDivRuns = singleDivRuns;
    this.clickoutHandlerOverride = (event) => {
      let pickers = [ ... document.querySelectorAll(".clock-picker") ];
      let didClickPicker = pickers.find(picker => picker.contains(event.target));
      let fillBlocks = this.$el.querySelector(".fill-blocks");
      let didClickClock = fillBlocks.contains(event.target);
      let shouldIgnoreClickout = didClickPicker || didClickClock;
      return shouldIgnoreClickout;
    }    
    return {
      store,
      lastBlock: null,
      selectedRun: null,
      selectedHour_: null,
      clockCopy: null
    }
  },
  computed: {
    selectedValue() {
      return this.selectedOption.value;
    },
    optionsLookup() {
      let map = {};
      this.options.forEach(option => map[option.value] = option);
      return map;
    },
    runs() {
      let runs = [];
      let clockStart = this.range ? this.range[0] : 0; 
      let clockEnd = this.range ? this.range[1] : 1440;
      let currentRun = { begin: clockStart, end: null, value: this.clock.value[clockStart] };
      // Performance note: Dereferencing 'this.clock.value' from within the loop can be very slow when iterating the whole clock.
      for (let values = this.clock.value, index = clockStart; index < clockEnd; index++) {
        let value = values[index];
        if (value != currentRun.value) {
          currentRun.end = index;
          runs.push(currentRun);
          currentRun = { begin: index, end: null, value };
        }
      };
      currentRun.end = clockEnd;
      runs.push(currentRun);
      return runs;
    },
    quarters() {
      let quarters = [];
      let clock = this.clock.value;
      let isOuterClock = (this.selectedHour == null);
      let maxBlocksPerQuarter = isOuterClock ? 8 : 15;
      let clockBegin = isOuterClock ? 0 : this.selectedHour;
      let clockLen = isOuterClock ? 1440 : 60;
      let numQuarters = clockLen / 15;
      for (let quarter = 0; quarter < numQuarters; quarter++) {
        let quarterBegin = clockBegin + quarter * 15;
        let quarterEnd = quarterBegin + 15;
        let runs = this.runs.filter(run =>
          (run.begin >= quarterBegin && run.begin < quarterEnd) || // Run starts somewhere in the quarter, or
          (run.end > quarterBegin && run.end <= quarterEnd) ||     // Run ends somewhere in the quarter, or
          (run.begin < quarterBegin && run.end > quarterEnd)       // Run begins before the quarter and ends after the quarter.
        );
        let blocks = [];
        if (runs.length <= maxBlocksPerQuarter) {
          blocks = runs.map((run, index) => {
            let runLen = run.end - run.begin;
            if (index == 0) runLen = Math.min(15, run.end - quarterBegin);
            if (index == runs.length - 1) runLen = Math.min(15, quarterEnd - run.begin);
            return {
              blends: [{ value: run.value, opacity: 1 }],
              flexGrow: runLen,
              minWidth: `${Math.min(3, Math.floor(8 / runs.length))}px`
            }
          });
        }
        else {
          for (let i = 0; i < maxBlocksPerQuarter; i++) {
            let blockLen = 15 / maxBlocksPerQuarter;
            let blockBegin = quarterBegin + (i * blockLen);
            let blockEnd = blockBegin + blockLen;
            let uniqueValuesInBlock = unique(clock.slice(blockBegin, blockEnd));
            let blends = uniqueValuesInBlock.map(value => {
              return { value, opacity: 1 / uniqueValuesInBlock.length };
            }); 
            blends[0].opacity = 1;
            blocks.push({
              blends,
              flexGrow: blockLen,
              minWidth: "1px"
            });
          }
        }
        quarters.push({
          runs,
          blocks,
          flexGrow: 15
        });
      }
      return quarters;
    },
    blockGroups() {
      let blockGroups = [];
      let currentGroup = { ... this.quarters[0] };
      this.quarters.forEach((quarter, index) => {
        if (index == 0) {
          return;
        }
        let isSameValue = (
          quarter.runs.length == 1 &&
          currentGroup.runs.length == 1 &&
          currentGroup.runs[0].value == quarter.runs[0].value
        );
        if (isSameValue) {
          currentGroup.flexGrow += 15;
        }
        else {
          blockGroups.push(currentGroup);
          currentGroup = { ... quarter };
        }
      });
      blockGroups.push(currentGroup);
      return blockGroups;
    },
    fillBlocks() {
      let blocks = [];
      let clockStart = this.range ? this.range[0] : 0;
      let clockLen = this.range ? this.range[1] - this.range[0] : 1440;
      let numBlocks = clockLen / this.fillSize;
      for (let i = 0; i < numBlocks; i++) {
        let startIndex = clockStart + (i * this.fillSize) / clockGranularity;
        let endIndex = clockStart + ((i + 1) * this.fillSize) / clockGranularity;
        blocks.push({
          startIndex,
          endIndex
        });
      };
      return blocks;
    },
    hourLabels() {
      let labels = null;
      // if (this.selectedHour == null) {
      //   labels = range(24).map(hour => {
      //     let formatted = this.formatTime(hour * 60);
      //     return `${formatted.hour}${formatted.meridiem.toLowerCase()}`;
      //   });
      // }
      // else {
        let isOuterClock = (this.range == null);
        let clockStart = isOuterClock ? 0 : this.range[0];
        let clockLen = isOuterClock ? 1440 : this.range[1] - this.range[0];
        let minutesPerLabel = isOuterClock ? 60 : (this.fillSize < 15 ? 5 : this.fillSize);
        let numLabels = clockLen / minutesPerLabel;
        labels = range(0, numLabels).map(index => {
          let minutesOffset = index * minutesPerLabel;
          let minutes = clockStart + minutesOffset;
          let formatted = this.formatTime(minutes);
          if ((minutes % 60) == 0 || index == 0)
            return `${formatted.hour}${formatted.meridiem}`;
          else
            return `:${formatted.minutes}`
        })
      // }
      return labels;
    },
    currentTimeMinutes() {
      let system = store.sessions[0].system;
      let d = zoneDate(new Date(system.utcTime.value * 1000), system.timezone.value);
      let hours = d.getUTCHours();
      let minutes = hours * 60 + d.getUTCMinutes();
      return minutes;
    },
    currentTimeString() {
      return this.formatTime(this.currentTimeMinutes).timeString;
    },
    showingCurrentTimeIndicator() {
      let isOuterClock = (this.selectedHour == null);
      let clockBegin = isOuterClock ? 0 : this.selectedHour;
      let clockEnd = isOuterClock ? 1440 : this.selectedHour + 60;
      return isOuterClock || (
        this.currentTimeMinutes >= clockBegin && 
        this.currentTimeMinutes <= clockEnd
      );
    },
    currentTimeIndicator() {
      let isOuterClock = (this.selectedHour == null);
      let clockLen = isOuterClock ? 1440 : 60;
      let clockBegin = isOuterClock ? 0 : this.selectedHour;
      let currentTimeOffset = this.currentTimeMinutes - clockBegin;
      return {
        position: "absolute",
        left: `calc(${(currentTimeOffset / clockLen) * 100}% - 10px)`
      };
    },
    formattedRunStart() {
      return this.formatTime(this.selectedRun.begin);
    },
    formattedRunEnd() {
      return this.formatTime(this.selectedRun.end);
    }
  },
  watch: {
    selectedOption() {
      this.selectedRun = null;
      if (this.selectedOption == null)
        this.selectedHour_ = null;
    },
    selectedHour_() {
      this.$refs.clockPopover?.repositionPopover();
    }
  },
  mounted() {
    document.body.addEventListener("click", this.clickoutHandler);
    document.body.addEventListener("touchbegin", this.clickoutHandler);
  },
  unmounted() {
    document.body.removeEventListener("click", this.clickoutHandler);
    document.body.removeEventListener("touchbegin", this.clickoutHandler);
  },
  methods: {
    showingRunHandles(run) {
      let isInnerClock = (this.range != null);
      let isSmallFillSize = (this.fillSize < 15);
      return (
        isInnerClock &&
        isSmallFillSize &&
        run == this.selectedRun
      );
    },
    onMouseUp(block) {
      // Show the clock popover if the fillSize is high.
      let isOuterClock = (this.range == null);
      let isSmallFillSize = (this.fillSize <= 15);
      if (isOuterClock && isSmallFillSize) {
        this.selectedHour_ = block.startIndex - (block.startIndex % 60); 
      }
    },
    adjustSelectedHour(diff) {
      this.selectedHour_ += diff;
      this.$refs.clockPopover.repositionPopover();
    },
    onMouseDown(block, blockIndex) {
      // Don't apply the selected value if it isn't relevant to this clock (eg sensor purge on bins with no CO2 sensor).
      if (!this.options.includes(this.selectedOption))
        return;

      // Make a copy of the old clock values. Replace any occurrences of the selected value in the copy.
      this.clockCopy = this.clock.value.slice();
      this.fillBlocks.forEach(block => {
        let { startIndex, endIndex } = block;
        let blockValues = this.clockCopy.slice(startIndex, endIndex);
        let fillValue = blockValues.find(value => value != this.selectedValue) || 0;
        for (let i = startIndex; i < endIndex; i++) {
          if (this.clockCopy[i] == this.selectedValue)
            this.clockCopy[i] = fillValue;
        }
      });

      let isOuterClock = (this.range == null);
      if (isOuterClock && this.fillSize <= 15) {
        this.selectedHour_ = block.startIndex - (block.startIndex % 60);
      }
      else {
        // Write the selected value to the block's region of the clock.
        for (let i = block.startIndex; i < block.endIndex; i++) {
          this.clock.value[i] = this.selectedValue;
        }
        this.onChange();
      }

      draggingClockbar = this;
      this.lastBlock = block;

      if (this.selectedHour != null) {
        this.fillValue(null, block);
      }
    },
    onMouseOver(block, blockIndex) {
      // Don't apply the selected value if it isn't relevant to this clock (eg sensor purge on bins with no CO2 sensor).
      if (!this.options.includes(this.selectedOption))
        return;
      
      if (draggingClockbar == this) {
        // Select the block's corresponding hour.
        let isOuterClock = (this.range == null);
        if (isOuterClock && this.fillSize <= 15) {
          this.selectedHour_ = block.startIndex - (block.startIndex % 60); 
        }
        this.fillValue(this.lastBlock, block);
        this.lastBlock = block;
      }
    },
    onTouchMove(event) {
      // Don't apply the selected value if it isn't relevant to this clock (eg sensor purge on bins with no CO2 sensor).
      if (!this.options.includes(this.selectedOption))
        return;
      
      // Get the element under the touch-point.
      let touchPoint = event.touches[0];
      let el = document.elementFromPoint(touchPoint.clientX, touchPoint.clientY);

      // Early return if the touch-point wasn't on a block element belonging to this clockbar.
      let fillBlocks = [ ... this.$el.querySelectorAll(".fill-block") ];
      if (!fillBlocks.includes(el))
        return;

      // Select the block's corresponding hour.
      let isOuterClock = (this.range == null);
      let block = this.fillBlocks[el.getAttribute('blockIndex')];
      if (isOuterClock && this.fillSize <= 15) {
        this.selectedHour_ = block.startIndex - (block.startIndex % 60); 
      }

      // Fill the necessary blocks.
      this.fillValue(this.lastBlock, block);
      this.lastBlock = block;
    },
    fillValue(lastBlock, block) {
      if (this.selectedValue == null) return;
      if (lastBlock == null) lastBlock = block;
      
      let from = Math.min(lastBlock.startIndex, block.startIndex);
      let to = Math.max(lastBlock.endIndex, block.endIndex);
      for (let i = from; i < to; i++) {
        this.clock.value[i] = this.selectedValue;
      }
      this.onChange();
    },
    formatTime(timeOfDayInMinutes) {
      let hour = Math.floor(timeOfDayInMinutes / 60);
      let hourString = (hour % 12 == 0) ? 12 : hour % 12
      let minutes = Math.floor(timeOfDayInMinutes % 60);
      let minutesString = padStart(minutes, 2, '0');
      let meridiem = (hour < 12) ? 'am' : 'pm';
      return {
        hour: hourString,
        minutes: minutesString,
        meridiem,
        timeString: `${hourString}:${minutesString} ${meridiem}`
      };
    },
    formatTooltip(timeOfDayInMinutes) {
      return this.formatTime(Math.round(timeOfDayInMinutes)).timeString;
    },
    clickoutHandler(event) {
      // if (this.selectedHour_) {
      //   let elements = [
      //     this.$el.querySelector('.fill-blocks'),
      //     ... document.querySelectorAll('.clock-picker, .hour-group-popover')
      //   ];
      //   if (!elements.find(elem => elem.contains(event.target)))
      //     this.selectedHour_ = null;
      // }
    },
    blockGroupStyle(blockGroup) {
      return {
        flexGrow: blockGroup.flexGrow,
      };
    },
    blockStyle(block) {
      return {
        flexGrow: block.flexGrow,
        minWidth: block.minWidth
      };
    },
    blockBlendStyle(blend) {
      let option = this.optionsLookup[blend.value];
      let style = {
        background: this.committed ? option.color : "white",
        opacity: blend.opacity
      };
      if (option.striped && this.committed) {
        Object.assign(style, {
          ... stripePattern(option.color, this.options[0].color),
          backgroundAttachment: "fixed",
          backgroundPosition: "top left"
        })
      }
      return style;
    },
    clockPopoverStyle() {
      return {
        left: `${(this.selectedHour_ ) / 1440 * 100}%`
      };
    },
    popoverContainerStyle() {
      return {
        width: `${document.querySelector(".bins-scroll")?.clientWidth - 40}px`,
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 5px 10px 15px rgba(0, 0, 0, 0.1)"
      };
    },
    popoverClickoutIgnoreElems() {
      let pickers = [ ... document.querySelectorAll(".clock-picker") ];
      let fillBlocks = this.$refs.fillBlocks;
      return [ ... pickers, fillBlocks ];
    }
  }
}

</script>

<style scoped>
.clock-bar {
  user-select: none;
}
label {
  cursor: pointer;
  margin-left: 0.2in;
}
.toolbar {
  font-size: large;
  display: flex;
}
.hour-captions {
  display: flex;
  position: relative;
}
.hour-caption {
  flex-grow: 1;
  flex-basis: 0;
  min-width: 0;
  font-size: small;
  padding-left: 1px;
  border-left: 1px solid gray;
  white-space: nowrap;
  overflow: hidden;
}
.current-time-indicator {
  top: 40px;
  z-index: 3;
  padding-left: 5px;
  padding-right: 5px;
  border-radius: 10px; 
  border: 1px solid;
  background: rgba(245, 245, 245, 0.781);
  white-space: nowrap;
  pointer-events: none;
}
.block-groups,
.fill-blocks, 
.hour-groups,
.title {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
}
.block-group {
  display: flex;
  flex-basis: 0;
}
.block {
  position: relative;
}
.block-blend {
  position: absolute;
  width: 100%;
  height: 100%;
}
.run-handle {
  z-index: 5;
  padding: 1px;
  padding-left: 5px;
  padding-right: 5px;
  border: 1px solid #555;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.515);
  white-space: nowrap;
  transform: translateY(0.2in);
}
.run-handle-spacer {
  flex-grow: 1;
}
.fill-blocks {
  z-index: 2;
}
.fill-block {
  flex-grow: 1;
  flex-basis: 0;
  color: white;
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-right: 0;
  display: flex;
}
.fill-block:last-child {
  border-right: 1px solid gray;
}
.miniblock {
  height: 100%;
  flex-basis: 0;
  flex-grow: 1;
  flex-shrink: 1;
}
.blend-block {
  height: 100%;
  width: 20%;
  position: relative;
}
.blend-block div {
  position: absolute;
  height: 100%;
  width: 100%;
}
.clock-popover {
  position: absolute;
  width: 4%;
  height: 100%;
  outline: 2px solid #333;
  outline-offset: 2px;
  z-index: 2;
  pointer-events: none;
}
.hour-group {
  flex-grow: 1;
  flex-basis: 0;
}

.title {
  z-index: 5;
  align-items: center;
  justify-content: center;
  padding-left: 10px;
  font-weight: bold;
  font-size: large;
  color: #333;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  pointer-events: none;
}
</style>

<style>
.hour-group-popover {
  padding: 15px;
  padding-top: 20px;
  padding-bottom: 20px;
  background: white;
  z-index: 5;
  position: relative;
}
</style>